/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useQuery } from '@tanstack/react-query'
import { useState, useCallback, useEffect } from 'react'

import { systemInformationService } from '../../../services/systemInformationService'
import { persistService } from '../../../services/persist'
import type {
  UsePerformanceDataReturn,
  UseActiveProcessesReturn,
  UseServerVersionReturn,
  UseVersionFooterReturn,
  ActiveSessionsResponse
} from '../../../types/systemInformation'

// Custom hooks
export const usePerformanceData = (): UsePerformanceDataReturn => {
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['performance-data'],
    queryFn: systemInformationService.getPerformanceView,
    staleTime: 0, // Always consider data stale to allow refetch
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    retry: 2
  })

  const handleRefetch = useCallback(async () => {
    await refetch()
  }, [refetch])

  return {
    data: data || null,
    isLoading: isLoading || isFetching,
    error: error as Error | null,
    refetch: handleRefetch
  }
}

export const useActiveProcesses = (): UseActiveProcessesReturn => {
  const [isLoading, setIsLoading] = useState(false)
  const [backgroundStatuses, setBackgroundStatuses] = useState({})
  const [activeSessions, setActiveSessions] = useState<ActiveSessionsResponse | null>(null)
  const [error, setError] = useState<Error | null>(null)

  const refetch = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const [bgStatus, activeSessionsData] = await Promise.all([
        systemInformationService.getPerformanceViewBgStatus(),
        systemInformationService.getActiveSessionsStatus()
      ])

      setBackgroundStatuses(bgStatus.tasks)
      setActiveSessions(activeSessionsData)
    } catch (err) {
      setError(err as Error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  return {
    backgroundStatuses,
    activeSessions,
    isLoading,
    error,
    refetch
  }
}

export const useServerVersion = (): UseServerVersionReturn => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['server-version'],
    queryFn: systemInformationService.getServerVersion,
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: 2
  })

  return {
    data: data || null,
    isLoading,
    error: error as Error | null
  }
}

export const useVersionFooter = (): UseVersionFooterReturn => {
  const [showInFooter, setShowInFooter] = useState(() => {
    return persistService.getGlobal<boolean>('showVersionInFooter') ?? false
  })

  const toggleFooterDisplay = useCallback(() => {
    const newValue = !showInFooter
    persistService.setGlobal('showVersionInFooter', newValue)
    setShowInFooter(newValue)

    // Emit event for other components to listen to
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('showVersionInfoInFooterToggled', {
          detail: { showInFooter: newValue }
        })
      )
    }
  }, [showInFooter])

  // Listen for footer toggle events from other components
  useEffect(() => {
    if (typeof window === 'undefined') return

    const handleFooterToggle = (event: CustomEvent) => {
      const { showInFooter: newValue } = event.detail
      setShowInFooter(newValue)
    }

    window.addEventListener('showVersionInfoInFooterToggled', handleFooterToggle as EventListener)

    return () => {
      window.removeEventListener('showVersionInfoInFooterToggled', handleFooterToggle as EventListener)
    }
  }, [])

  return {
    showInFooter,
    toggleFooterDisplay
  }
}
